import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  mercantilScrapingListRuns,
  mercantilScrapingGetRun,
  mercantilScrapingRetry,
  mercantilScrapingMarcarRevision,
} from "../../../helpers/backend_helper";

const PAGE_SIZE = 10;
const AUTO_REFRESH_MS = 30_000;

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-VE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const fmtDuracion = (iniciado, finalizado) => {
  if (!iniciado || !finalizado) return "—";
  const ms = new Date(finalizado).getTime() - new Date(iniciado).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
};

const badgeOrigen = (origen) => {
  const map = {
    scheduler: "info",
    manual: "primary",
    retry: "warning",
  };
  return <Badge color={map[origen] || "secondary"}>{origen || "—"}</Badge>;
};

const badgeEstado = (doc) => {
  if (doc.saltada_por_lock) return <Badge color="secondary">Saltada (lock)</Badge>;
  if (doc.error_global) return <Badge color="danger">Error global</Badge>;
  if (!doc.finalizado) return <Badge color="info">En curso…</Badge>;
  if ((doc.fallidas_pendientes || 0) > 0)
    return <Badge color="warning">Requiere revisión</Badge>;
  return <Badge color="success">OK</Badge>;
};

const HistorialCorridas = () => {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detalleAbierto, setDetalleAbierto] = useState(null); // run doc
  const timerRef = useRef(null);

  const cargar = useCallback(async (nextSkip = 0, append = false) => {
    setLoading(true);
    try {
      const res = await mercantilScrapingListRuns({ limit: PAGE_SIZE, skip: nextSkip });
      const data = res?.data?.data || [];
      const t = res?.data?.total || 0;
      setTotal(t);
      setRuns((prev) => (append ? [...prev, ...data] : data));
      setSkip(nextSkip);
    } catch (e) {
      toast.error("No se pudo cargar el historial de corridas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar(0, false);
  }, [cargar]);

  // Auto-refresh si hay alguna corrida sin finalizar
  const hayEnCurso = useMemo(
    () => runs.some((r) => !r.finalizado && !r.saltada_por_lock),
    [runs]
  );
  useEffect(() => {
    if (!hayEnCurso) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      cargar(0, false);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [hayEnCurso, cargar]);

  const abrirDetalle = async (runId) => {
    try {
      const res = await mercantilScrapingGetRun(runId);
      const doc = res?.data?.data;
      if (doc) setDetalleAbierto(doc);
    } catch (e) {
      toast.error("No se pudo cargar el detalle de la corrida");
    }
  };

  const cerrarDetalle = () => setDetalleAbierto(null);

  const refrescarDetalle = async () => {
    if (!detalleAbierto?.run_id) return;
    try {
      const res = await mercantilScrapingGetRun(detalleAbierto.run_id);
      const doc = res?.data?.data;
      if (doc) setDetalleAbierto(doc);
    } catch {
      /* noop */
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">
            <i className="ri-history-line me-2"></i>
            Historial de corridas
          </h5>
          <small className="text-muted">
            Últimas corridas del scheduler y disparos manuales. Total registradas: {total}.
          </small>
        </div>
        <Button
          color="light"
          size="sm"
          onClick={() => cargar(0, false)}
          disabled={loading}
        >
          <i className="ri-refresh-line me-1"></i>
          Refrescar
        </Button>
      </div>

      <div className="table-responsive">
        <Table bordered hover size="sm" className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Inicio</th>
              <th>Origen</th>
              <th>Rango</th>
              <th>Duración</th>
              <th className="text-center">Listadas</th>
              <th className="text-center">Creadas</th>
              <th className="text-center">Omitidas</th>
              <th className="text-center">Fallidas</th>
              <th className="text-center">Pend. revisión</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && !loading && (
              <tr>
                <td colSpan={11} className="text-center text-muted py-4">
                  No hay corridas registradas todavía.
                </td>
              </tr>
            )}
            {runs.map((r) => {
              const rowCls =
                r.error_global
                  ? "table-danger"
                  : (r.fallidas_pendientes || 0) > 0
                  ? "table-warning"
                  : "";
              return (
                <tr key={r.run_id} className={rowCls}>
                  <td className="fs-12">{fmtFecha(r.iniciado)}</td>
                  <td>{badgeOrigen(r.origen)}</td>
                  <td className="fs-12">
                    {r.rango_desde || "—"} → {r.rango_hasta || "—"}
                  </td>
                  <td className="fs-12">{fmtDuracion(r.iniciado, r.finalizado)}</td>
                  <td className="text-center">{r.total_listadas ?? 0}</td>
                  <td className="text-center text-success fw-semibold">
                    {r.total_creadas ?? 0}
                  </td>
                  <td className="text-center">{r.total_omitidas ?? 0}</td>
                  <td className="text-center text-danger">
                    {r.total_fallidas ?? 0}
                  </td>
                  <td className="text-center">
                    {(r.fallidas_pendientes || 0) > 0 ? (
                      <Badge color="warning">{r.fallidas_pendientes}</Badge>
                    ) : (
                      <span className="text-muted">0</span>
                    )}
                  </td>
                  <td>{badgeEstado(r)}</td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      onClick={() => abrirDetalle(r.run_id)}
                    >
                      <i className="ri-eye-line me-1"></i>
                      Ver detalle
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <div className="text-muted fs-12">
          Mostrando {runs.length} de {total}
        </div>
        {runs.length < total && (
          <Button
            size="sm"
            color="light"
            onClick={() => cargar(skip + PAGE_SIZE, true)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" /> Cargando…
              </>
            ) : (
              <>Cargar más</>
            )}
          </Button>
        )}
      </div>

      <DetalleCorridaModal
        run={detalleAbierto}
        onClose={cerrarDetalle}
        onChanged={async () => {
          await refrescarDetalle();
          cargar(0, false);
        }}
      />
    </div>
  );
};

const DetalleCorridaModal = ({ run, onClose, onChanged }) => {
  const isOpen = Boolean(run);
  const [accionEnCurso, setAccionEnCurso] = useState({}); // key: identificador

  if (!run) return null;

  const creadas = (run.detalle || []).filter((o) => o.estatus === "creada");
  const fallidas = (run.detalle || []).filter((o) => o.estatus === "fallida");

  const setBusy = (id, val) =>
    setAccionEnCurso((prev) => ({ ...prev, [id]: val }));

  const handleReintentar = async (identificador) => {
    setBusy(identificador, "retry");
    try {
      const res = await mercantilScrapingRetry(identificador, run.run_id);
      const data = res?.data?.data;
      if (data?.estatus === "creada") {
        toast.success(`${identificador}: creada en Farme.`);
      } else if (data?.estatus === "omitida") {
        toast.info(`${identificador}: ya existía en Farme.`);
      } else {
        toast.error(
          `${identificador}: ${data?.error_detalle || "Falló nuevamente"}`
        );
      }
      await onChanged?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Error reintentando ${identificador}`
      );
    } finally {
      setBusy(identificador, null);
    }
  };

  const handleRevision = async (identificador, estado) => {
    let nota = null;
    if (estado === "descartada") {
      nota = window.prompt(
        `Motivo para descartar la orden ${identificador} (opcional):`,
        ""
      );
      if (nota === null) return; // canceló
    }
    setBusy(identificador, estado);
    try {
      await mercantilScrapingMarcarRevision(run.run_id, identificador, {
        estado,
        nota: nota || null,
      });
      toast.success(`${identificador}: marcada como ${estado}`);
      await onChanged?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Error actualizando ${identificador}`
      );
    } finally {
      setBusy(identificador, null);
    }
  };

  const badgeRevision = (estado) => {
    const e = estado || "pendiente";
    const color =
      e === "resuelta"
        ? "success"
        : e === "descartada"
        ? "secondary"
        : e === "reintentando"
        ? "info"
        : "warning";
    return <Badge color={color}>{e}</Badge>;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered>
      <ModalHeader toggle={onClose}>
        <span className="fw-semibold">Corrida Mercantil</span>{" "}
        <small className="text-muted font-monospace ms-2">
          {run.run_id}
        </small>
      </ModalHeader>
      <ModalBody>
        <div className="row g-2 mb-3 fs-13">
          <div className="col-md-3">
            <strong>Inicio:</strong> {fmtFecha(run.iniciado)}
          </div>
          <div className="col-md-3">
            <strong>Fin:</strong> {fmtFecha(run.finalizado)}
          </div>
          <div className="col-md-3">
            <strong>Duración:</strong> {fmtDuracion(run.iniciado, run.finalizado)}
          </div>
          <div className="col-md-3">
            <strong>Origen:</strong> {badgeOrigen(run.origen)}
          </div>
          <div className="col-md-3">
            <strong>Rango:</strong> {run.rango_desde} → {run.rango_hasta}
          </div>
          <div className="col-md-9">
            <strong>Estado:</strong> {badgeEstado(run)}
            {run.error_global && (
              <span className="ms-2 text-danger fs-12">
                {run.error_global}
              </span>
            )}
          </div>
        </div>

        <div className="row text-center g-2 mb-4">
          <div className="col">
            <div className="fs-22 fw-semibold">{run.total_listadas ?? 0}</div>
            <div className="text-muted fs-12">Listadas</div>
          </div>
          <div className="col">
            <div className="fs-22 fw-semibold text-success">
              {run.total_creadas ?? 0}
            </div>
            <div className="text-muted fs-12">Creadas</div>
          </div>
          <div className="col">
            <div className="fs-22 fw-semibold text-warning">
              {run.total_omitidas ?? 0}
            </div>
            <div className="text-muted fs-12">Omitidas</div>
          </div>
          <div className="col">
            <div className="fs-22 fw-semibold text-danger">
              {run.total_fallidas ?? 0}
            </div>
            <div className="text-muted fs-12">Fallidas</div>
          </div>
          <div className="col">
            <div className="fs-22 fw-semibold">
              {run.fallidas_pendientes ?? 0}
            </div>
            <div className="text-muted fs-12">Pend. revisión</div>
          </div>
        </div>

        {/* Órdenes creadas */}
        {creadas.length > 0 && (
          <>
            <h6 className="text-success">
              <i className="ri-checkbox-circle-line me-1"></i>
              Órdenes creadas ({creadas.length})
            </h6>
            <div className="table-responsive mb-4" style={{ maxHeight: 220, overflowY: "auto" }}>
              <Table size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Identificador</th>
                    <th>Duración</th>
                    <th>Adjuntos</th>
                    <th>Solicitud en Farme</th>
                  </tr>
                </thead>
                <tbody>
                  {creadas.map((o) => (
                    <tr key={o.identificador}>
                      <td className="font-monospace">{o.identificador}</td>
                      <td className="fs-12">{o.duracion_ms}ms</td>
                      <td className="fs-12">
                        <Badge color={o.informe_subido ? "success" : "secondary"} className="me-1">
                          informe
                        </Badge>
                        <Badge color={o.recipe_subido ? "success" : "secondary"} className="me-1">
                          recipe
                        </Badge>
                        <Badge color={o.captura_subida ? "success" : "secondary"}>
                          captura
                        </Badge>
                      </td>
                      <td>
                        {o.solicitud_id ? (
                          <Link
                            to={`/solicitud/ver/${o.solicitud_id}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="ri-external-link-line me-1"></i>
                            Abrir
                          </Link>
                        ) : (
                          <span className="text-muted fs-12">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {/* Órdenes fallidas */}
        {fallidas.length > 0 && (
          <>
            <h6 className="text-danger">
              <i className="ri-error-warning-line me-1"></i>
              Órdenes fallidas ({fallidas.length})
            </h6>
            <div className="table-responsive" style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Identificador</th>
                    <th>Categoría</th>
                    <th>Detalle</th>
                    <th>Revisión</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {fallidas.map((o) => {
                    const busy = accionEnCurso[o.identificador];
                    const revEstado = o.revision_estado || "pendiente";
                    const readonly = revEstado === "descartada" || revEstado === "resuelta";
                    return (
                      <tr key={o.identificador}>
                        <td className="font-monospace">{o.identificador}</td>
                        <td>
                          <Badge color="danger-subtle" className="text-danger">
                            {o.error_categoria || "UNKNOWN"}
                          </Badge>
                        </td>
                        <td className="fs-12 text-muted" style={{ maxWidth: 380 }}>
                          {o.error_detalle || "—"}
                          {o.revision_nota && (
                            <div className="fst-italic mt-1">
                              Nota: {o.revision_nota}
                              {o.revision_por ? ` — ${o.revision_por}` : ""}
                            </div>
                          )}
                        </td>
                        <td>{badgeRevision(revEstado)}</td>
                        <td className="text-end">
                          <div className="d-flex gap-1 justify-content-end flex-wrap">
                            <Button
                              size="sm"
                              color="primary"
                              disabled={!!busy || readonly}
                              onClick={() => handleReintentar(o.identificador)}
                            >
                              {busy === "retry" ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <i className="ri-refresh-line me-1"></i>
                                  Reintentar
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              color="success"
                              outline
                              disabled={!!busy || readonly}
                              onClick={() => handleRevision(o.identificador, "resuelta")}
                            >
                              {busy === "resuelta" ? (
                                <Spinner size="sm" />
                              ) : (
                                <>Marcar resuelta</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              disabled={!!busy || readonly}
                              onClick={() => handleRevision(o.identificador, "descartada")}
                            >
                              {busy === "descartada" ? (
                                <Spinner size="sm" />
                              ) : (
                                <>Descartar</>
                              )}
                            </Button>
                            {readonly && (
                              <Button
                                size="sm"
                                color="light"
                                disabled={!!busy}
                                onClick={() => handleRevision(o.identificador, "pendiente")}
                              >
                                Reabrir
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {creadas.length === 0 && fallidas.length === 0 && (
          <div className="text-center text-muted py-3">
            La corrida no registró órdenes creadas ni fallidas.
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default HistorialCorridas;
